import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { finalize, retry, throwError, timer } from 'rxjs';
import { environment } from '@environments/environment';
import { BackendStatusService } from '@app/services/backend-status.service';
import { SILENCE_LOADING, COLDSTART_RETRY } from '@app/services/backend-http-context';

/**
 * Interceptor per il cold-start del backend (Cloud Run).
 *
 * - Traccia ogni richiesta verso `environment.backendDomain` in un contatore
 *   condiviso (`BackendStatusService`) così la UI può mostrare un feedback.
 *   Il tracking può essere silenziato per singola richiesta con il context
 *   token `SILENCE_LOADING`.
 * - Ritenta le sole GET (idempotenti) quando il backend non risponde per un
 *   cold-start: status 0 (rete/CORS a istanza spenta) o 502/503/504 (gateway).
 *   Backoff lineare, max 4s. Ogni altro status (400/401/404/500…) viene
 *   propagato subito, senza retry. Il retry può essere forzato anche per
 *   richieste non-GET con il context token `COLDSTART_RETRY`.
 */
const RETRYABLE_STATUSES = new Set([0, 502, 503, 504]);
const MAX_RETRIES = 3;

export const coldStartInterceptorFn: HttpInterceptorFn = (req, next) => {
  if (!req.url.startsWith(environment.backendDomain)) {
    return next(req);
  }

  const silence = req.context.get(SILENCE_LOADING);
  const forceRetry = req.context.get(COLDSTART_RETRY);
  const status = inject(BackendStatusService);
  if (!silence) status.begin();

  const count = req.method === 'GET' || forceRetry ? MAX_RETRIES : 0;

  return next(req).pipe(
    retry({
      count,
      delay: (error: unknown, retryCount: number) => {
        if (error instanceof HttpErrorResponse && RETRYABLE_STATUSES.has(error.status)) {
          return timer(Math.min(1000 * retryCount, 4000));
        }
        return throwError(() => error);
      },
    }),
    finalize(() => {
      if (!silence) status.end();
    })
  );
};
